<template>
	<div class="m-switchReveal">
		<div class="m-switchReveal__row">
			<label for="switch-reveal" class="m-switchReveal__label">{{ label }}</label>
			<button
				id="switch-reveal"
				class="m-switchReveal__switch"
				:class="{ '-disabled': disabled }"
				type="button"
				role="switch"
				:aria-checked="modelValue ? 'true' : 'false'"
				:aria-label="ariaLabel || label"
				:disabled="disabled"
				@click="toggle()"
				@keydown.enter.prevent="toggle()"
				@keydown.space.prevent="toggle()"
			>
				<span class="m-switchReveal__track" aria-hidden="true">
					<span class="m-switchReveal__thumb" :style="thumbStyle" />
				</span>
			</button>
		</div>

		<div v-if="modelValue" class="m-switchReveal__reveal" :aria-live="announce ? 'polite' : null">
			<div class="m-licensePlate">
				<div class="m-licensePlate__inner">
					<div class="m-licensePlate__country" aria-hidden="true">
						<EuFlag />
						<span class="m-licensePlate__code">{{ country }}</span>
					</div>
					<div class="m-licensePlate__slots">
						<input
							id="license-plate-input"
							ref="plateInput"
							class="m-licensePlate__input"
							type="text"
							inputmode="text"
							:placeholder="placeholder"
							:aria-label="ariaLabelPlate || 'Kenteken'"
							:value="displayValue"
							@input="onInput"
							@keydown="onKeyDown"
							@paste="onPaste"
							@blur="onBlur"
						/>
					</div>
				</div>
				<div
					class="m-licensePlate__feedback u-fs16"
					v-if="rdwStatus !== 'idle' && rdwMessage"
					:class="{
						'-loading': rdwStatus === 'loading',
						'-found': rdwStatus === 'found',
						'-notFound': rdwStatus === 'notFound',
						'-error': rdwStatus === 'error'
					}"
					role="status"
					aria-live="polite"
					:aria-busy="rdwStatus === 'loading' ? 'true' : null"
				>{{ rdwMessage }}</div>
				<div class="m-switchReveal__description u-fs14">
					<span>Versnel je aanvraag door het kenteken in te vullen. Dit is niet verplicht. </span> <br/>
					<button
						type="button"
						class="m-switchReveal__toggleLink"
						@click="toggleInputMode"
						:aria-label="inputMode === 'search' ? 'Schakel over naar vrij invullen' : 'Schakel over naar zoeken'"
					>
						{{ inputMode === 'search' ? 'Vrij invullen' : 'Zoeken' }}
					</button>
				</div>
			</div>

			<slot />
			
		</div>
	</div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import EuFlag from './EuFlag.vue';
import { processPlate, cleanPlate } from '../utils/licensePlate.js';

const props = defineProps( {
	label: {
		type: String,
		default: '',
	},
	ariaLabel: {
		type: String,
		default: '',
	},
	ariaLabelPlate: {
		type: String,
		default: '',
	},
	country: {
		type: String,
		default: 'NL',
	},
	announce: {
		type: Boolean,
		default: true,
	},
	modelValue: {
		type: Boolean,
		default: false,
	},
	plate: {
		type: String,
		default: '',
	},
	inputMode: {
		type: String,
		default: 'search',
		validator: ( value ) => [ 'search', 'free' ].includes( value ),
	},
	disabled: {
		type: Boolean,
		default: false,
	},
} );

const emit = defineEmits( [ 'update:modelValue', 'update:plate', 'update:inputMode', 'update:rdwStatus' ] );

const toggle = () => {
	// Don't toggle if disabled
	if ( props.disabled ) {
		return;
	}
	emit( 'update:modelValue', ! props.modelValue );
};

const thumbStyle = computed( () => {
	return {
		transform: props.modelValue ? 'translateX(16px)' : 'translateX(0)',
	};
} );

const plateInput = ref( null );

// Input mode state - sync with prop
const inputMode = computed( {
	get: () => props.inputMode,
	set: ( value ) => {
		emit( 'update:inputMode', value );
	},
} );

// RDW feedback state
const rdwStatus = ref( 'idle' ); // 'idle' | 'loading' | 'found' | 'notFound' | 'error'
const rdwMessage = ref( '' );
let rdwTimer = null;
let rdwAbortController = null;

const processedPlate = computed( () => {
	if ( ! props.plate ) {
		return {
			cleaned: '',
			sidecode: null,
			formatted: '',
			isValid: false,
		};
	}
	if ( inputMode.value === 'free' ) {
		// In free mode, just return the plate as-is (uppercase)
		return {
			cleaned: props.plate.toUpperCase().replace( /[^A-Z0-9]/g, '' ),
			sidecode: null,
			formatted: props.plate.toUpperCase(),
			isValid: false,
		};
	}
	return processPlate( props.plate );
} );

const displayValue = computed( () => {
	if ( inputMode.value === 'free' ) {
		return props.plate.toUpperCase();
	}
	return processedPlate.value.formatted || props.plate;
} );

const placeholder = computed( () => {
	return inputMode.value === 'free' ? 'Vrij invullen' : 'Bijv. PZ-99-ZH';
} );

let cursorInfo = null;

const onKeyDown = ( event ) => {
	// In free mode, allow any alphanumeric input (max 20 chars)
	if ( inputMode.value === 'free' ) {
		const specialKeys = [ 'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter' ];
		if ( specialKeys.includes( event.key ) || event.ctrlKey || event.metaKey || event.altKey ) {
			return;
		}

		const char = event.key;
		if ( ! /^[A-Za-z0-9]$/.test( char ) ) {
			event.preventDefault();
			return false;
		}

		const currentInputValue = event.target.value || '';
		const cleaned = currentInputValue.replace( /[^A-Z0-9]/gi, '' );
		
		if ( cleaned.length >= 20 ) {
			event.preventDefault();
			return false;
		}
		return;
	}

	// Search mode: existing validation logic
	const specialKeys = [ 'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter' ];
	if ( specialKeys.includes( event.key ) || event.ctrlKey || event.metaKey || event.altKey ) {
		if ( event.key === 'Backspace' || event.key === 'Delete' ) {
			cursorInfo = {
				position: event.target.selectionStart || 0,
				operation: event.key === 'Backspace' ? 'backspace' : 'delete',
			};
		}
		return;
	}

	const char = event.key;
	const isDash = char === '-';
	
	cursorInfo = {
		position: event.target.selectionStart || 0,
		operation: isDash ? 'dash' : 'add',
	};

	if ( ! /^[A-Za-z0-9-]$/.test( char ) ) {
		cursorInfo = null;
		event.preventDefault();
		return false;
	}

	const currentInputValue = event.target.value || '';
	const cursorPos = event.target.selectionStart || 0;

	if ( isDash ) {
		if ( currentInputValue[ cursorPos - 1 ] === '-' || currentInputValue[ cursorPos ] === '-' ) {
			cursorInfo = null;
			event.preventDefault();
			return false;
		}
	}

	const currentCleaned = cleanPlate( currentInputValue );
	
	if ( currentCleaned.length >= 7 ) {
		cursorInfo = null;
		event.preventDefault();
		return false;
	}
	
	if ( ! isDash ) {
		const simulatedValue = currentInputValue.slice( 0, cursorPos ) + char + currentInputValue.slice( cursorPos );
		const simulatedCleaned = cleanPlate( simulatedValue );
		
		if ( simulatedCleaned.length > 7 ) {
			cursorInfo = null;
			event.preventDefault();
			return false;
		}
	}
};

const onInput = ( event ) => {
	const inputValue = event.target.value;
	const inputCursorPos = event.target.selectionStart || 0;
	
	if ( inputMode.value === 'free' ) {
		// Free mode: simple uppercase conversion, max 20 chars
		const cleaned = inputValue.toUpperCase().replace( /[^A-Z0-9]/g, '' ).slice( 0, 20 );
		emit( 'update:plate', cleaned );
		nextTick( () => {
			if ( plateInput.value ) {
				// Keep cursor position
				const newPos = Math.min( inputCursorPos, cleaned.length );
				plateInput.value.setSelectionRange( newPos, newPos );
			}
		} );
		return;
	}

	// Search mode: existing logic
	const processed = processPlate( inputValue );

	const oldFormatted = displayValue.value;

	emit( 'update:plate', processed.cleaned );

	nextTick( () => {
		if ( plateInput.value && processed.formatted ) {
			if ( cursorInfo ) {
				const oldCursorPos = cursorInfo.position;
				const operation = cursorInfo.operation;
				cursorInfo = null;

				if ( operation === 'dash' ) {
					let oldCharCount = 0;
					for ( let i = 0; i < Math.min( oldCursorPos, oldFormatted.length ); i++ ) {
						if ( oldFormatted[ i ] !== '-' ) {
							oldCharCount++;
						}
					}
					
					let newCharCount = 0;
					let mappedOldPos = 0;
					for ( let i = 0; i < processed.formatted.length; i++ ) {
						if ( processed.formatted[ i ] !== '-' ) {
							newCharCount++;
						}
						if ( newCharCount <= oldCharCount ) {
							mappedOldPos = i + 1;
						}
					}
					
					let newPosition = mappedOldPos;
					
					if ( newPosition < processed.formatted.length && processed.formatted[ newPosition ] === '-' ) {
						newPosition++;
					} else {
						newPosition++;
					}
					
					newPosition = Math.min( newPosition, processed.formatted.length );
					
					if ( newPosition <= oldCursorPos ) {
						newPosition = Math.min( oldCursorPos + 1, processed.formatted.length );
					}
					
					newPosition = Math.max( 0, Math.min( newPosition, processed.formatted.length ) );
					plateInput.value.setSelectionRange( newPosition, newPosition );
					return;
				}

				let charsBeforeCursor = 0;
				for ( let i = 0; i < Math.min( oldCursorPos, oldFormatted.length ); i++ ) {
					if ( oldFormatted[ i ] !== '-' ) {
						charsBeforeCursor++;
					}
				}

				if ( operation === 'add' ) {
					charsBeforeCursor++;
				} else if ( operation === 'backspace' ) {
				} else if ( operation === 'delete' ) {
				}

				let newPosition = processed.formatted.length;
				let charCount = 0;
				
				for ( let i = 0; i < processed.formatted.length; i++ ) {
					if ( processed.formatted[ i ] !== '-' ) {
						charCount++;
						if ( charCount === charsBeforeCursor ) {
							newPosition = i + 1;
							if ( newPosition < processed.formatted.length && processed.formatted[ newPosition ] === '-' ) {
								newPosition++;
							}
							break;
						}
						if ( charCount < charsBeforeCursor && i === processed.formatted.length - 1 ) {
							newPosition = i + 1;
							break;
						}
					}
				}
				
				newPosition = Math.max( 0, Math.min( newPosition, processed.formatted.length ) );
				
				plateInput.value.setSelectionRange( newPosition, newPosition );
			} else {
				plateInput.value.setSelectionRange( processed.formatted.length, processed.formatted.length );
			}
		}
	} );
};

const onPaste = ( event ) => {
	event.preventDefault();
	const pastedText = ( event.clipboardData || window.clipboardData ).getData( 'text' );
	if ( pastedText ) {
		if ( inputMode.value === 'free' ) {
			const cleaned = pastedText.toUpperCase().replace( /[^A-Z0-9]/g, '' ).slice( 0, 20 );
			emit( 'update:plate', cleaned );
		} else {
			const processed = processPlate( pastedText );
			emit( 'update:plate', processed.cleaned );
		}
		nextTick( () => {
			if ( plateInput.value ) {
				plateInput.value.focus();
				plateInput.value.select();
			}
		} );
	}
};

const onBlur = () => {
};

const toggleInputMode = () => {
	const newMode = inputMode.value === 'search' ? 'free' : 'search';
	// Emit the mode change
	emit( 'update:inputMode', newMode );
	// Clear plate value when switching modes
	emit( 'update:plate', '' );
	// Clear RDW status
	rdwStatus.value = 'idle';
	rdwMessage.value = '';
	if ( rdwAbortController ) {
		rdwAbortController.abort();
		rdwAbortController = null;
	}
	if ( rdwTimer ) {
		clearTimeout( rdwTimer );
		rdwTimer = null;
	}
	// Focus input after mode switch
	nextTick( () => {
		if ( plateInput.value ) {
			plateInput.value.focus();
		}
	} );
};

watch( () => props.modelValue, ( newValue ) => {
	if ( newValue ) {
		nextTick( () => {
			if ( plateInput.value ) {
				plateInput.value.focus();
			}
		} );
	} else {
		// Clear plate value when trade-in is turned off
		emit( 'update:plate', '' );
		// Clear RDW status
		rdwStatus.value = 'idle';
		rdwMessage.value = '';
		if ( rdwAbortController ) {
			rdwAbortController.abort();
			rdwAbortController = null;
		}
		if ( rdwTimer ) {
			clearTimeout( rdwTimer );
			rdwTimer = null;
		}
	}
} );

// RDW lookup: throttle on valid cleaned plate and only when revealed
watch(
	() => ( { isOpen: props.modelValue, cleaned: processedPlate.value.cleaned, isValid: processedPlate.value.isValid, mode: inputMode.value } ),
	( state ) => {
		// Skip API lookup in free mode
		if ( state.mode === 'free' ) {
			if ( rdwAbortController ) {
				rdwAbortController.abort();
				rdwAbortController = null;
			}
			if ( rdwTimer ) {
				clearTimeout( rdwTimer );
				rdwTimer = null;
			}
			rdwStatus.value = 'idle';
			rdwMessage.value = '';
			return;
		}

		// Clear message when not open or not valid
		if ( ! state.isOpen || ! state.isValid ) {
			if ( rdwAbortController ) {
				rdwAbortController.abort();
				rdwAbortController = null;
			}
			if ( rdwTimer ) {
				clearTimeout( rdwTimer );
				rdwTimer = null;
			}
			rdwStatus.value = 'idle';
			rdwMessage.value = '';
			return;
		}

		// Throttle: schedule after 500ms
		if ( rdwTimer ) {
			clearTimeout( rdwTimer );
		}
		rdwStatus.value = 'idle';
		rdwMessage.value = '';
		const plate = state.cleaned.toUpperCase();
		rdwTimer = setTimeout( async () => {
			rdwTimer = null;
			// Store whether input had focus before API call
			const hadFocus = plateInput.value && document.activeElement === plateInput.value;
			// Abort any in-flight request
			if ( rdwAbortController ) {
				rdwAbortController.abort();
			}
			rdwAbortController = new AbortController();
			const signal = rdwAbortController.signal;
			try {
				rdwStatus.value = 'loading';
				rdwMessage.value = 'Bezig met zoeken…';
				const url = `https://opendata.rdw.nl/resource/m9d7-ebf2.json?kenteken=${ encodeURIComponent( plate ) }`;
				const response = await fetch( url, { signal } );
				const data = await response.json();
				if ( Array.isArray( data ) && data.length > 0 ) {
					const vehicle = data[ 0 ] || {};
					const merk = vehicle.merk || '';
					const benaming = vehicle.handelsbenaming || '';
					const info = [ merk, benaming ].filter( Boolean ).join( ' ' ).trim();
					rdwStatus.value = 'found';
					rdwMessage.value = info || 'Voertuig gevonden.';
				} else {
					rdwStatus.value = 'notFound';
					rdwMessage.value = 'Auto niet gevonden. Geen probleem. Waarschijnlijk is de auto dusdanig nieuw dat deze nog niet in het systeem staat. Check je kenteken goed, als het klopt, komt het goed.';
				}
			} catch ( err ) {
				if ( err && err.name === 'AbortError' ) {
					// ignored: superseded by new request
					return;
				}
				rdwStatus.value = 'error';
				rdwMessage.value = 'Er ging iets mis bij het ophalen van voertuiggegevens.';
			} finally {
				rdwAbortController = null;
				// Restore focus if input had focus before the API call
				if ( hadFocus && plateInput.value ) {
					nextTick( () => {
						if ( plateInput.value ) {
							plateInput.value.focus();
							// Restore cursor position to end
							const length = plateInput.value.value.length;
							plateInput.value.setSelectionRange( length, length );
						}
					} );
				}
			}
		}, 500 );
	},
	{ immediate: true, deep: true }
);

// Emit RDW status changes to parent component
watch( rdwStatus, ( newStatus ) => {
	emit( 'update:rdwStatus', newStatus );
}, { immediate: true } );

</script>
